/** White-particle shatter (death) and coalesce (respawn) effects using Canvas 2D circle textures. */
import Phaser from 'phaser';
import { ensureCircleTexture } from '../ui/CrispUtil';
import { ConfigLoader } from '../systems/ConfigLoader';
import { TAU } from '../utils/Math';

/** Spawns white circle particles that explode outward on death and converge inward on respawn. */
export class DeathParticleEffect {
  private scene: Phaser.Scene;
  private particles: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Spawn white particles at (x, y) and tween them outward. Resolves when all finish. */
  shatter(x: number, y: number, depth: number): Promise<void> {
    this.cleanup();
    const cfg = ConfigLoader.combat.screenEffects.playerDeath.shatter;
    const texKey = ensureCircleTexture(this.scene, cfg.particleRadius);
    const sparkTex = ensureCircleTexture(this.scene, cfg.sparkle.particleRadius);
    const totalCount = cfg.particleCount + cfg.sparkle.particleCount;

    return new Promise((resolve) => {
      let completed = 0;
      const onDone = () => {
        completed++;
        if (completed >= totalCount) {
          this.cleanup();
          resolve();
        }
      };

      // Primary burst
      for (let i = 0; i < cfg.particleCount; i++) {
        const angle = (TAU * i) / cfg.particleCount + (Math.random() - 0.5) * 0.4;
        const speed = cfg.minSpeed + Math.random() * (cfg.maxSpeed - cfg.minSpeed);
        const dist = speed * (cfg.durationMs / 1000);

        const img = this.scene.add.image(x, y, texKey)
          .setDepth(depth)
          .setTintFill(0xffffff);
        this.particles.push(img);

        this.scene.tweens.add({
          targets: img,
          x: x + Math.cos(angle) * dist,
          y: y + Math.sin(angle) * dist,
          alpha: { from: 1, to: 0, start: cfg.fadeStartRatio, ease: 'Sine.easeIn' },
          scaleX: { from: 1, to: 0.2 },
          scaleY: { from: 1, to: 0.2 },
          duration: cfg.durationMs,
          ease: 'Quad.easeOut',
          onComplete: onDone,
        });
      }

      // Sparkle layer — smaller, denser, slightly delayed
      const sp = cfg.sparkle;
      for (let i = 0; i < sp.particleCount; i++) {
        const angle = Math.random() * TAU;
        const speed = sp.minSpeed + Math.random() * (sp.maxSpeed - sp.minSpeed);
        const dist = speed * (sp.durationMs / 1000);

        const img = this.scene.add.image(x, y, sparkTex)
          .setDepth(depth)
          .setAlpha(0)
          .setTintFill(0xffffff);
        this.particles.push(img);

        this.scene.tweens.add({
          targets: img,
          x: x + Math.cos(angle) * dist,
          y: y + Math.sin(angle) * dist,
          alpha: { from: 0.8, to: 0, start: sp.fadeStartRatio, ease: 'Sine.easeIn' },
          scaleX: { from: 0.8, to: 0 },
          scaleY: { from: 0.8, to: 0 },
          duration: sp.durationMs,
          delay: sp.delayMs,
          ease: 'Cubic.easeOut',
          onComplete: onDone,
        });
      }
    });
  }

  /** Spawn white particles scattered around (x, y) and tween them inward. Resolves when all arrive. */
  coalesce(x: number, y: number, depth: number): Promise<void> {
    this.cleanup();
    const cfg = ConfigLoader.combat.screenEffects.playerDeath.coalesce;
    const texKey = ensureCircleTexture(this.scene, cfg.particleRadius);

    return new Promise((resolve) => {
      let completed = 0;
      for (let i = 0; i < cfg.particleCount; i++) {
        const angle = (TAU * i) / cfg.particleCount + (Math.random() - 0.5) * 0.4;
        const startX = x + Math.cos(angle) * cfg.scatterRadius;
        const startY = y + Math.sin(angle) * cfg.scatterRadius;

        const img = this.scene.add.image(startX, startY, texKey)
          .setDepth(depth)
          .setAlpha(0)
          .setTintFill(0xffffff);
        this.particles.push(img);

        this.scene.tweens.add({
          targets: img,
          x,
          y,
          alpha: { from: 0, to: 1 },
          duration: cfg.durationMs,
          ease: 'Quad.easeIn',
          onComplete: () => {
            completed++;
            if (completed >= cfg.particleCount) {
              this.cleanup();
              resolve();
            }
          },
        });
      }
    });
  }

  /** Remove all particle images and kill their tweens. Safe to call multiple times. */
  destroy(): void {
    this.cleanup();
  }

  private cleanup(): void {
    for (const img of this.particles) {
      this.scene.tweens.killTweensOf(img);
      img.destroy();
    }
    this.particles.length = 0;
  }
}
